import React, { useState, useEffect } from "react";
import { Spinner, Table, Form } from "react-bootstrap";
import "../../Styles/yellocalfcss/pagecollect.css";
import ApiConfig from "../../api/ApiConfig";
import { simplePostCall } from "../../api/ApiServices";
import ScaleLoader from "react-spinners/ScaleLoader";

const CollectPayment = () => {
  const [studentList, setStudentList] = useState([]);
  const [invoiceList, setInvoiceList] = useState([]);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [isFetchingInvoices, setIsFetchingInvoices] = useState(false);
  
  // New State for Errors
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    studentId: "",
    paymentDate: new Date().toISOString().split("T")[0],
    paymentMode: "",
    bankTxnNo: "BTXN" + Math.floor(Math.random() * 1000000),
    amount: "",
  });

  // --- 1. Load Initial Student List ---
  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const response = await simplePostCall(
          ApiConfig.studentlist,
          JSON.stringify({
            customer_id: Number(localStorage.getItem("customer_id")),
            api_key: localStorage.getItem("api_key"),
          }),
        );
        if (response?.studentList) setStudentList(response.studentList);
      } catch (error) {
        console.error("Error fetching students", error);
      } finally {
        setIsFetching(false);
      }
    };
    fetchStudents();
  }, []);

  // --- 2. Fetch Invoices ---
  const fetchStudentInvoices = async (studentId) => {
    if (!studentId) {
      setInvoiceList([]);
      return;
    }
    setIsFetchingInvoices(true);
    try {
      const response = await simplePostCall(
        ApiConfig.pendingInvoice,
        JSON.stringify({
          customer_id: Number(localStorage.getItem("customer_id")),
          api_key: localStorage.getItem("api_key"),
          user_id: Number(localStorage.getItem("user_id")),
          language: "en",
          student_search: studentId,
          status_search: "pending",
        }),
      );

      if (response?.result) {
        setInvoiceList(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching invoices", error);
    } finally {
      setIsFetchingInvoices(false);
    }
  };

  // --- 3. Validation Logic ---
  const validateForm = () => {
    let newErrors = {};

    if (!formData.studentId) newErrors.studentId = "Please select a student.";
    if (!formData.paymentMode) newErrors.paymentMode = "Select a payment mode.";
    if (!formData.paymentDate) newErrors.paymentDate = "Date is required.";
    
    // Amount Validations
    const amountNum = parseFloat(formData.amount);
    if (!formData.amount || amountNum <= 0) {
      newErrors.amount = "Enter a valid amount greater than 0.";
    }

    // Invoice Selection Validation
    if (selectedInvoiceIds.length === 0) {
      newErrors.invoices = "You must select at least one invoice.";
    }

    // Advanced Check: Amount vs Invoice Balance
    const selectedTotal = invoiceList
      .filter(inv => selectedInvoiceIds.includes(inv.fee_invoice_id))
      .reduce((sum, inv) => sum + parseFloat(inv.balance || 0), 0);

    if (amountNum > selectedTotal) {
      newErrors.amount = `Amount cannot exceed the total balance of selected invoices (${selectedTotal}).`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleStudentChange = (e) => {
    const id = e.target.value;
    setFormData({ ...formData, studentId: id });
    setSelectedInvoiceIds([]); 
    setErrors({ ...errors, studentId: "" }); // Clear error on change
    fetchStudentInvoices(id);
  };

  const handleInvoiceSelection = (invoiceId) => {
    setErrors({ ...errors, invoices: "" });
    setSelectedInvoiceIds((prev) =>
      prev.includes(invoiceId)
        ? prev.filter((id) => id !== invoiceId)
        : [...prev, invoiceId],
    );
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) setErrors({ ...errors, [name]: "" }); // Clear error on typing
  };

  // --- 4. Final API Call ---
  const createcollectfee = async (e) => {
    e.preventDefault();

    if (!validateForm()) return; // Stop if validation fails

    setIsLoading(true);
    const requestBody = {
      customer_id: Number(localStorage.getItem("customer_id")),
      api_key: localStorage.getItem("api_key"),
      user_id: Number(localStorage.getItem("user_id")),
      language: "en",
      student_id: Number(formData.studentId),
      payment_mode: formData.paymentMode,
      transaction_payment_for: "fees",
      transaction_mode: "bank_transfer",
      bank_transaction_no: formData.bankTxnNo,
      date: formData.paymentDate,
      invoice_ids: selectedInvoiceIds,
      paying_amount: Number(formData.amount),
      sandbox: 0,
    };

    try {
      const response = await simplePostCall(
        ApiConfig.collectparentpaymen,
        JSON.stringify(requestBody),
      );
      if (response?.result) {
        alert(response.message || "Payment Collected Successfully!");
        setFormData({
          studentId: "",
          paymentDate: new Date().toISOString().split("T")[0],
          paymentMode: "",
          bankTxnNo: "BTXN" + Math.floor(Math.random() * 1000000),
          amount: "",
        });
        setInvoiceList([]);
        setSelectedInvoiceIds([]);
      } else {
        alert(response?.message || "Error processing payment");
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="collectpayment-page-container my-2">
      <div className="collectpayment-page-header">
        <h2 className="collectpayment-page-title">Collect Payment</h2>
      </div>

      {isFetching ? (
        <div className="d-flex justify-content-center">
          <ScaleLoader color="#F4BB44" style={{ textAlign: "center", padding: "10px" }} />
        </div>
      ) : (
        <div className="collectpayment-card">
          <form className="collectpayment-payment-form" onSubmit={createcollectfee}>
            
            {/* Student Selection */}
            <div className="collectpayment-form-row">
              <div className="collectpayment-form-group collectpayment-full-width">
                <label>Student Name <span className="collectpayment-required">*</span></label>
                <select
                  className={`form-select ${errors.studentId ? 'is-invalid' : ''}`}
                  name="studentId"
                  value={formData.studentId}
                  onChange={handleStudentChange}
                >
                  <option value="">Select Student</option>
                  {studentList.map((s) => (
                    <option key={s.student_id} value={s.student_id}>{s.student_name}</option>
                  ))}
                </select>
                {errors.studentId && <div className="text-danger small mt-1">{errors.studentId}</div>}
              </div>
            </div>

            {/* Invoices Table */}
            {formData.studentId && (
              <div className="invoice-section my-4">
                <h5 className={errors.invoices ? "text-danger" : ""}>Pending Invoices</h5>
                {isFetchingInvoices ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <>
                    <Table striped bordered hover responsive size="sm" className={errors.invoices ? "border-danger" : ""}>
                      <thead>
                        <tr>
                          <th>Select</th>
                          <th>Invoice ID</th>
                          <th>Date</th>
                          <th>Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoiceList.map((inv) => (
                          <tr key={inv.fee_invoice_id}>
                            <td>
                              <Form.Check
                                type="checkbox"
                                checked={selectedInvoiceIds.includes(inv.fee_invoice_id)}
                                onChange={() => handleInvoiceSelection(inv.fee_invoice_id)}
                              />
                            </td>
                            <td>{inv.fee_invoice_id}</td>
                            <td>{inv.fee_invoice_date}</td>
                            <td className="text-danger font-weight-bold">{inv.balance}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    {errors.invoices && <div className="text-danger small">{errors.invoices}</div>}
                  </>
                )}
              </div>
            )}

            {/* Amount and Date */}
            <div className="collectpayment-form-row">
              <div className="collectpayment-form-group collectpayment-half-width">
                <label>Amount to Pay <span className="collectpayment-required">*</span></label>
                <input
                  type="number"
                  name="amount"
                  className={errors.amount ? "border-danger" : ""}
                  value={formData.amount}
                  onChange={handleChange}
                  placeholder="0.00"
                />
                {errors.amount && <div className="text-danger small mt-1">{errors.amount}</div>}
              </div>
              <div className="collectpayment-form-group collectpayment-half-width">
                <label>Payment Date <span className="collectpayment-required">*</span></label>
                <input
                  type="date"
                  name="paymentDate"
                  className={errors.paymentDate ? "border-danger" : ""}
                  value={formData.paymentDate}
                  onChange={handleChange}
                />
                {errors.paymentDate && <div className="text-danger small mt-1">{errors.paymentDate}</div>}
              </div>
            </div>

            {/* Mode and Txn No */}
            <div className="collectpayment-form-row">
              <div className="collectpayment-form-group collectpayment-half-width">
                <label>Payment Mode <span className="collectpayment-required">*</span></label>
                <select
                  className={`form-select ${errors.paymentMode ? 'is-invalid' : ''}`}
                  name="paymentMode"
                  value={formData.paymentMode}
                  onChange={handleChange}
                >
                  <option value="">Select Mode</option>
                  <option value="cash">Cash</option>
                  <option value="bank_transfer">Bank Transfer</option>
                </select>
                {errors.paymentMode && <div className="text-danger small mt-1">{errors.paymentMode}</div>}
              </div>
              <div className="collectpayment-form-group collectpayment-half-width">
                <label>Transaction No</label>
                <input
                  type="text"
                  name="bankTxnNo"
                  value={formData.bankTxnNo}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="collectpayment-form-footer">
              <button
                type="submit"
                className="collectpayment-submit-btn"
                disabled={isLoading}
              >
                {isLoading ? <Spinner animation="border" size="sm" /> : "Collect Payment"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default CollectPayment;